import { useEffect, useMemo, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";

import { authTokenAtom } from "atoms/authAtoms";
import { errorAtom } from "atoms/error";
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { toUserMessage } from "utils/apiErrorMessage";
import { copyToConfigAtom } from "atoms/Ltool/copyToConfigAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { fetchDimensions, fetchDimensionsHev } from "api/fetchLtool";
import { submitDimensions, submitDimensionsHev, type FailedCopyItem } from "api/submitLtool";
import { PHENOMENON_IDS, UNIT_IDS } from "utils/constsLtool";
import { PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";
import { mappingAtom } from "atoms/mappingAtoms";
import { getMappingLabel, buildLabeledOptions } from "utils/mappingLabel";
import { buildDimensionsParams } from "utils/dimensionsForm";
import {
    FailedItemsList,
    buildCopyErrorMessage,
    buildResultMessage,
    classifySummary,
} from "components/FailedItemsList";

export const ManualDimensions: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(copyToConfigAtom);
    const mapping = useAtomValue(mappingAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const setError = useSetAtom(errorAtom);
    const { showSuccess, showError, showWarning } = useSnackbar();

    // BEV も HEV と同じ phenomenon 単位・flat 形式（drive_mode 非依存、諸元データ仕様変更 #26）
    const isHev = powertrain === 'hev';
    const phenomenonIds: readonly string[] = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;
    // Unit ID ドロップダウンを定義順で表示する（マトリクスの行順と一致させる）
    const unitOptions = useMemo(
        () => buildLabeledOptions(mapping, 'unit_id', UNIT_IDS),
        [mapping],
    );

    const [loading, setLoading] = useState(false);
    const [unitId, setUnitId] = useState<string>("");
    const [tireRadius, setTireRadius] = useState<string>("");
    // gear_ratio / motor_order は phenomenon_id ごとの flat Dict（BEV/HEV 共通、運転モード非依存）
    const [gearRatios, setGearRatios] = useState<Record<string, string>>({});
    const [motorOrders, setMotorOrders] = useState<Record<string, string>>({});
    const [resultMessage, setResultMessage] = useState<string | null>(null);
    const [failedItems, setFailedItems] = useState<FailedCopyItem[]>([]);

    const {
        project_id,
        destination_id,
        lot_id,
        phase_id,
        calculation_id,
    } = config;

    const configComplete = Boolean(
        project_id &&
        destination_id &&
        lot_id &&
        phase_id &&
        calculation_id
    );

    // HEV は gear 選択も前提
    const ready = configComplete && Boolean(unitId) && (!isHev || Boolean(gearId));

    // 登録済み諸元データをデフォルトとして反映
    useEffect(() => {
        const reset = () => {
            setTireRadius("");
            setGearRatios(Object.fromEntries(phenomenonIds.map((p) => [p, ""])));
            setMotorOrders(Object.fromEntries(phenomenonIds.map((p) => [p, ""])));
        };

        if (!ready) {
            reset();
            return;
        }

        let cancelled = false;
        const configForFetch = { project_id, destination_id, lot_id, phase_id, calculation_id };

        const fetchDefaults = async () => {
            try {
                const data = isHev
                    ? await fetchDimensionsHev(authToken, configForFetch, unitId, gearId!)
                    : await fetchDimensions(authToken, configForFetch, unitId);
                if (cancelled) return;
                setTireRadius(Number.isFinite(data.tire_radius) ? String(data.tire_radius) : "");
                const nextGearRatios = Object.fromEntries(phenomenonIds.map((p) => [p, ""])) as Record<string, string>;
                Object.entries(data.gear_ratio || {}).forEach(([phenomenonId, value]) => {
                    if (phenomenonId in nextGearRatios && Number.isFinite(value)) {
                        nextGearRatios[phenomenonId] = String(value);
                    }
                });
                setGearRatios(nextGearRatios);
                const nextMotorOrders = Object.fromEntries(phenomenonIds.map((p) => [p, ""])) as Record<string, string>;
                Object.entries(data.motor_order || {}).forEach(([phenomenonId, value]) => {
                    if (phenomenonId in nextMotorOrders && Number.isFinite(value)) {
                        nextMotorOrders[phenomenonId] = String(value);
                    }
                });
                setMotorOrders(nextMotorOrders);
            } catch (e) {
                if (cancelled) return;
                reset();
                console.error("諸元データの取得に失敗しました:", e);
                showError("登録済み諸元データの取得に失敗しました。入力欄は空のままです。");
            }
        };

        fetchDefaults();

        return () => {
            cancelled = true;
        };
    }, [
        authToken,
        unitId,
        gearId,
        isHev,
        phenomenonIds,
        project_id,
        destination_id,
        lot_id,
        phase_id,
        calculation_id,
        ready,
        showError,
    ]);

    // バリデーション
    const dimensionsParams = buildDimensionsParams(tireRadius, gearRatios, motorOrders, phenomenonIds);
    const paramsValid = dimensionsParams !== null;

    useEffect(() => {
        setResultMessage(null);
        setFailedItems([]);
    }, [unitId, gearId, isHev]);

    const handleSubmit = async () => {
        if (!unitId) {
            showWarning("Unit IDを選択してください。");
            return;
        }
        if (isHev && !gearId) {
            showWarning("HEV では Gear を選択してください。");
            return;
        }
        if (!dimensionsParams) {
            showWarning(
                "tire_radius を正しく入力し、各現象は gear_ratio と motor_order を両方入力するか両方未入力にしてください（少なくとも1件は入力が必要です）。",
            );
            return;
        }

        setLoading(true);
        setError(null);
        setResultMessage(null);
        setFailedItems([]);

        try {
            const response = isHev
                ? await submitDimensionsHev(authToken, config, unitId, gearId!, dimensionsParams)
                : await submitDimensions(authToken, config, unitId, dimensionsParams);
            if (response.status >= 400) {
                setResultMessage(buildCopyErrorMessage(response));
                setFailedItems(response.failed_items ?? []);
                return;
            }

            const failed = response.failed_items ?? [];
            if (!response.summary) {
                if (failed.length > 0) {
                    setResultMessage("一部の登録に失敗しました。");
                    setFailedItems(failed);
                    return;
                }
                showSuccess(response.message || "諸元データを登録しました。");
            } else if (classifySummary(response.summary) === "all_success") {
                showSuccess(buildResultMessage(response.summary));
            } else {
                setResultMessage(buildResultMessage(response.summary));
                setFailedItems(failed);
            }
        } catch (e) {
            setError(toUserMessage(e));
            showError(toUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const disabled =
        loading ||
        !configComplete ||
        !unitId ||
        (isHev && !gearId) ||
        !paramsValid;

    return (
        <Box p={2}>
            <Typography>UIから諸元データを登録します。</Typography>
            {isHev && !gearId && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    HEV では先に Gear を選択してください。
                </Typography>
            )}

            <Stack spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                    <InputLabel id="unit-id-label">Unit ID</InputLabel>
                    <Select
                        labelId="unit-id-label"
                        label="Unit ID"
                        value={unitId}
                        onChange={(e) => setUnitId(e.target.value as string)}
                    >
                        {unitOptions.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="tire_radius (float > 0)"
                    type="number"
                    value={tireRadius}
                    onChange={(e) => setTireRadius(e.target.value)}
                    fullWidth
                />

                <Box>
                    <Typography variant="subtitle2">gear_ratio / motor_order（現象IDごと）</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                        運転モード非依存
                    </Typography>
                    <Stack spacing={2}>
                        {phenomenonIds.map((phenomenonId) => (
                            <Box key={phenomenonId} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {getMappingLabel(mapping, 'phenomenon_id', phenomenonId)}
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                    <TextField
                                        label="gear_ratio (float > 0)"
                                        type="number"
                                        value={gearRatios[phenomenonId] ?? ""}
                                        onChange={(e) =>
                                            setGearRatios((prev) => ({ ...prev, [phenomenonId]: e.target.value }))
                                        }
                                        fullWidth
                                    />
                                    <TextField
                                        label="motor_order (float > 0)"
                                        type="number"
                                        value={motorOrders[phenomenonId] ?? ""}
                                        onChange={(e) =>
                                            setMotorOrders((prev) => ({ ...prev, [phenomenonId]: e.target.value }))
                                        }
                                        fullWidth
                                    />
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                <Button
                    variant="contained"
                    sx={{ alignSelf: "flex-start", minWidth: 160 }}
                    disabled={disabled}
                    onClick={handleSubmit}
                    startIcon={loading ? <CircularProgress size={18} /> : undefined}
                >
                    {loading ? "登録中..." : "諸元データ登録"}
                </Button>
                {resultMessage && (
                    <Box>
                        <Typography color="error" variant="body2">
                            {resultMessage}
                        </Typography>
                        <FailedItemsList items={failedItems} />
                    </Box>
                )}
            </Stack>
        </Box>
    );
};
